import { useCallback, useContext, useMemo } from "react"
import { isPlainObject } from "lodash"
import { useQueryClient } from "@tanstack/react-query"
import { useAccountAlias } from "resources/account"
import { useResourceCurrency } from "resources/currencies"
import RestDataIntegrator from "gatherers/rest-data-integrator"
import { useStore } from "react-redux"
import type { ReduxState } from "model/state"
import { useAppSelector } from "hooks/redux"
import { useDashboardId } from "../../context/dashboard-id"
import { useModelId } from "../../hooks/use-model-id"
import { EMPTY_MODULES } from "../../store/panels-slice"
import { useRemoveModule } from "../../hooks/use-remove-module"
import { useUpdateModule } from "../../hooks/use-update-module"
import ArtifactRenderer from "renderers/ai-artifact"
import { ERROR, OK } from "helpers/data-statuses"
import type { DataStatus } from "helpers/data-statuses"
import type { ModuleRenderDependencies } from "../types"
import { createArtifactGatherer } from "./gatherer"
import type { AgentArguments, Artifact, PositionSet } from "./types"
import type { GridInitialState } from "@mui/x-data-grid-premium"
import { NotFoundError } from "api/errors"

import "./style.scss"

function unwrapSingleQueryResult<T>(data: T[]): T | null {
  return Array.isArray(data) && data.length > 0 ? data[0] : null
}

function getPositionSetWithAlias(
  selectedPositionSet: PositionSet | undefined,
  alias: string | undefined,
): PositionSet | undefined {
  if (!selectedPositionSet) return undefined

  return {
    ...selectedPositionSet,
    id: alias || selectedPositionSet.id,
  }
}

export default function AiArtifactModule({
  TradeContext,
  moduleId,
}: Omit<ModuleRenderDependencies, "config"> & { moduleId: string }) {
  const queryClient = useQueryClient()
  const accountAlias = useAccountAlias()
  const dashboardId = useDashboardId()
  const store = useStore<ReduxState>()
  const modules = useAppSelector((s) => s.dashboard.panels[dashboardId]?.modules ?? EMPTY_MODULES)
  const removeModule = useRemoveModule()
  const updateModule = useUpdateModule()
  const { startDate, endDate, base, instrument, instrumentsMetadata } = useContext(TradeContext)
  const modelId = useModelId()
  const [currency] = useResourceCurrency(modelId, instrument)

  const module = useMemo(
    () => modules.find(m => m.id === moduleId),
    [modules, moduleId],
  )
  const config = module?.config || {}
  const { pinnedId, kind, isPinning } = config
  const title = typeof config.title === "string" ? config.title.trim() : ""

  const agentArguments: AgentArguments = useMemo(() => {
    const originalMetadata = instrumentsMetadata?.original
    const instrumentAlias = originalMetadata && "alias" in originalMetadata
      ? originalMetadata.alias
      : undefined

    return {
      modelId: modelId ?? undefined,
      // Type assertion: In practice, instrument and base are always SelectedTypedInstrument (with id/type)
      // when used with AI artifacts, never SelectedInlineInstrument or SelectedRulesInstrument
      positionSet: getPositionSetWithAlias(instrument as PositionSet, instrumentAlias),
      base: base as PositionSet | undefined,
      startDate,
      endDate,
    }
  }, [modelId, instrument, instrumentsMetadata, base, startDate, endDate])

  const gatherer = useMemo(() => {
    if (!pinnedId) return null
    return createArtifactGatherer({
      pinnedId,
      agentArguments,
      accountAlias,
      kind,
    })
  }, [pinnedId, agentArguments, accountAlias, kind])

  const queries = useMemo(() => {
    return gatherer ? [gatherer] : []
  }, [gatherer])

  const handleRetry = useCallback(() => {
    if (!gatherer) return
    queryClient.invalidateQueries({ queryKey: gatherer.key })
  }, [gatherer, queryClient])

  // `moduleId` is the stable prop; `module` is recomputed by `useMemo` every
  // time the modules array changes (any pin/unpin in the dashboard), so
  // depending on it would invalidate this callback — and therefore
  // `rendererProps` and `RestDataIntegrator`'s children — on unrelated edits.
  const onDataGridStateChange = useCallback(
    (dataGridState: GridInitialState) => {
      updateModule(moduleId, { config: { dataGridState } })
    },
    [updateModule, moduleId],
  )

  const rendererProps = useMemo(
    () => ({
      currency,
      title: title || undefined,
      dataGridState: isPlainObject(config.dataGridState) ? (config.dataGridState as GridInitialState) : undefined,
      isPinning,
      onDataGridStateChange,
      onRetry: handleRetry,
    }),
    [currency, title, config.dataGridState, isPinning, onDataGridStateChange, handleRetry],
  )

  // Read `hasError` straight from the store inside the callback (bypassing
  // React's render cycle) so a rapid ERROR → OK transition in the same tick
  // doesn't get collapsed against a stale closure value and leave the
  // error chrome stuck on.
  const onStatusChange = useCallback((status: DataStatus) => {
    if (status !== ERROR && status !== OK) return
    const currentHasError = store.getState().dashboard.panels[dashboardId]?.modules
      .find((m) => m.id === moduleId)?.config?.hasError === true
    if (status === ERROR && !currentHasError) {
      updateModule(moduleId, { config: { hasError: true } })
    } else if (status === OK && currentHasError) {
      updateModule(moduleId, { config: { hasError: false } })
    }
  }, [store, dashboardId, moduleId, updateModule])

  const handleQueryError = useCallback((error: unknown) => {
    if (error instanceof NotFoundError && module) {
      removeModule(module)
    }
  }, [module, removeModule])

  // If pinning has already completed, missing gatherer/pinnedId is a terminal error.
  if (!isPinning && (!pinnedId || !gatherer)) {
    return <div className="ai-artifact-module__error">We couldn&apos;t load this module. Please try removing and pinning it again.</div>
  }

  return (
    <div className="ai-artifact-module">
      <RestDataIntegrator<Artifact>
        queries={queries}
        Renderer={ArtifactRenderer}
        rendererProps={rendererProps}
        dataTransform={unwrapSingleQueryResult}
        onStatusChange={onStatusChange}
        onError={handleQueryError}
        key={`artifact-${moduleId}-${pinnedId}`}
      />
    </div>
  )
}
